import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";

export type DivisionAddress = {
  id?: number;
  line1?: string;
  line2?: string;
  line3?: string;
  line4?: string;
  countryISOCode?: string;
  email?: string;
  telephone?: string;
};

export type DivisionAccreditationBanner = {
  image?: string;
  contentType?: string;
  fileName?: string;
};

export type DivisionReportTextDto = {
  id?: number;
  reportTextId: number;
  divisionId: number | null;
  centreId: number | null;
  content: string;
  format: number;
  isDeleted: boolean;
  forWhomId?: number | null;
};

const parsePositiveInt = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.trunc(value);
  }
  if (typeof value === "string" && value.trim()) {
    const n = Number.parseInt(value.trim(), 10);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  return undefined;
};

const parseNullableInt = (value: unknown): number | null => {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return Number.parseInt(value.trim(), 10);
  }
  return null;
};

export function normalizeDivisionReportTextDto(dto: any): DivisionReportTextDto {
  const id = parsePositiveInt(dto?.id ?? dto?.Id);
  const reportTextId =
    parsePositiveInt(dto?.reportTextId ?? dto?.ReportTextId) ?? 0;

  const forWhomId = parseNullableInt(
    dto?.forWhomId ??
      dto?.ForWhomId ??
      dto?.recipientType ??
      dto?.RecipientType
  );

  const formatRaw = dto?.format ?? dto?.Format;
  const format =
    typeof formatRaw === "number" && Number.isFinite(formatRaw) ? formatRaw : 1;

  const base: DivisionReportTextDto = {
    reportTextId,
    divisionId: (dto?.divisionId ?? dto?.DivisionId ?? null) as number | null,
    centreId: (dto?.centreId ?? dto?.CentreId ?? null) as number | null,
    content: String(dto?.content ?? dto?.Content ?? ""),
    format,
    isDeleted: Boolean(dto?.isDeleted ?? dto?.IsDeleted ?? false),
    forWhomId,
  };

  if (id !== undefined) {
    base.id = id;
  }

  return base;
}

export type DivisionDto = {
  id: number;
  name: string;
  termsAndConditions?: string;
  groupsPaymentTerms?: string;
  websiteUrl?: string;
  address?: DivisionAddress | null;
  accreditationBanner?: DivisionAccreditationBanner | null;
  isActive: boolean;
  visaLetterNote?: string | null;
  divisionReportTexts?: DivisionReportTextDto[];
};

export type DivisionCreateDto = {
  name: string;
  termsAndConditions?: string;
  groupsPaymentTerms?: string;
  isActive?: boolean;
  websiteUrl: string;
  address?: DivisionAddress | null;
  accreditationBanner?: DivisionAccreditationBanner | null;
  visaLetterNote?: string | null;
  visaLetterNoteFormat?: number;
  headOfficeEmailAddress?: string;
  headOfficeTelephoneNo?: string;
  divisionReportTexts?: DivisionReportTextDto[];
};

export type DivisionUpdateDto = {
  name: string;
  termsAndConditions?: string;
  groupsPaymentTerms?: string;
  isActive: boolean;
  websiteUrl: string;
  address?: DivisionAddress | null;
  accreditationBanner?: DivisionAccreditationBanner | null;
  visaLetterNote?: string | null;
  visaLetterNoteFormat?: number;
  headOfficeEmailAddress?: string;
  headOfficeTelephoneNo?: string;
  divisionReportTexts?: DivisionReportTextDto[];
};

export async function getActiveDivisions(): Promise<DivisionDto[]> {
  const api = await getLocalApi();
  return api.fetchList(
    (apiConfig as any).get_product_divisions,
    (dto: any) => dto as DivisionDto,
    "Failed to fetch active divisions"
  );
}

export async function createDivision(payload: DivisionCreateDto): Promise<Response> {
  const api = await getLocalApi();
  const path = (apiConfig as any).save_product_division;
  const res = await api.call(path, "PUT", JSON.stringify(payload));
  if (!res) throw new Error("No response from API");
  return res;
}

export async function updateDivision(id: number, payload: DivisionUpdateDto): Promise<Response> {
  const api = await getLocalApi();
  const path = (apiConfig as any).get_product_division_by_id.replace(
    "{divisionId}", encodeURIComponent(String(id))
  );
  const res = await api.call(path, "PUT", JSON.stringify(payload));
  if (!res) throw new Error("No response from API");
  return res;
}